import { Swatch } from '@/components/blocks/Swatch';
import { CurrentStoreLocator } from '@/components/content/FacetNavigation/parts/CurrentStoreLocator';
import {
	FacetRatingStars,
	isRatingFacet,
} from '@/components/content/FacetNavigation/parts/FacetRatingStars';
import {
	currentStoreLocatorLabelSX,
	facetNavigationFacetEntryFormControlLabelSX,
	facetNavigationFacetEntrySwatchSX,
} from '@/components/content/FacetNavigation/styles/facetEntry/formControlLabel';
import { FACET_STORE_LOCATOR_PREFIX } from '@/data/constants/facet';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { ProductFacetEntry } from '@/data/types/Product';
import { extractElasticField } from '@/data/utils/facetUrlMapper';
import { combineSX } from '@/utils/combineSX';
import { parseHTML } from '@/utils/parseHTMLModern';
import { Checkbox, FormControlLabel } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Props = {
	entry: ProductFacetEntry;
	facetValue?: string;
};

export const FacetNavigationEntry: FC<Props> = ({ entry, facetValue }) => {
	const productFilterNLS = useLocalization('ProductFilter');
	const { selectedFacet: selectedFacetFromQuery, onFacetSelectChange } = useContext(
		ContentContext
	) as ReturnType<typeof useFacetNavigation>;

	// Optimistic local copy of the applied facets, so the checkbox flips instantly
	// on click instead of waiting for router.query to commit after the shallow push.
	const [selectedFacet, setSelectedFacet] = useState<string[]>(selectedFacetFromQuery);
	useEffect(() => {
		// Re-sync once the query-derived selection commits.
		setSelectedFacet(selectedFacetFromQuery);
	}, [selectedFacetFromQuery]);

	const isRating = isRatingFacet(facetValue ?? '');
	const isStoreLocatorFacet = entry.value?.includes(FACET_STORE_LOCATOR_PREFIX);

	const isSelected = useMemo(() => {
		if (selectedFacet === undefined) return false;
		if (!!entry.extendedData.name) {
			const decodedEntries = selectedFacet.map((v) => extractElasticField(v).replace('.', ''));
			const entryValue = entry.extendedData.name?.toLowerCase();
			return decodedEntries.indexOf(entryValue) > -1;
		}

		return selectedFacet.indexOf(entry.value) > -1;
	}, [selectedFacet, entry.extendedData.name, entry.value]);

	const onChange = useCallback(
		(event: any) => {
			// onFacetSelectChange returns the freshly-computed set (sync, via ref);
			// apply it optimistically to the local copy of selectedFacet.
			const newSelectedFacet = onFacetSelectChange(event, entry);
			setSelectedFacet(newSelectedFacet);
		},
		[entry, onFacetSelectChange]
	);

	const entryLabel = isStoreLocatorFacet
		? productFilterNLS.Labels.availableInStoreOnly.t()
		: entry.label;
	const entryLabelText = `${parseHTML(entryLabel)} ${entry.count > 0 ? `(${entry.count})` : ''}`;

	return entry.image ? (
		<Swatch
			aria-label={entry.label}
			image={entry.image}
			onClick={onChange}
			size="large"
			title={entry.label}
			selected={isSelected}
			data-testid={`product-filter-${entry.value.toLowerCase()}-swatch`}
			id={`product-filter-${entry.value.toLowerCase()}-swatch`}
			className={isSelected ? 'filter-selected' : undefined}
			sx={facetNavigationFacetEntrySwatchSX}
		/>
	) : (
		<>
			<FormControlLabel
				sx={combineSX([
					facetNavigationFacetEntryFormControlLabelSX,
					isStoreLocatorFacet && currentStoreLocatorLabelSX,
				])}
				className={isSelected ? 'filter-selected' : undefined}
				control={<Checkbox checked={isSelected} onChange={onChange} />}
				label={
					isRating ? <FacetRatingStars label={entry.label} count={entry.count} /> : entryLabelText
				}
				id={`${entry.label} (${entry.count})`}
				data-testid={`${entry.label} (${entry.count})`}
			/>
			{isStoreLocatorFacet && <CurrentStoreLocator />}
		</>
	);
};
